/**
 * Signage validity helpers.
 *
 * Galleries and individual gallery images can carry an optional
 * "valid from" / "valid to" window. Both bounds are optional and independent:
 * a missing (or unparseable) bound means "no limit" on that side, so an item
 * with neither bound is valid forever. An item is hidden only when a bound
 * actually exists and "now" falls outside it.
 */

// Matches a date-only value like "2026-01-15" (no time component).
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Unwrap a raw scalar from either a plain value or a Firestore-shaped value
 * ({ stringValue } / { timestampValue } / { integerValue }). Returns null when
 * empty/absent.
 *
 * @param {*} value
 * @returns {string|number|null}
 */
const unwrap = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  if (typeof value === "object") {
    const raw =
      value.stringValue ??
      value.timestampValue ??
      value.integerValue ??
      null;
    return raw === undefined || raw === "" ? null : raw;
  }
  return value;
};

/**
 * Parse a validity bound into a Date, or null when empty/invalid.
 * A date-only value ("2026-01-15") is anchored to local time: the start of the
 * day for a "from" bound, the end of the day for a "to" bound (so the whole end
 * day stays valid). Full datetimes / timestamps are parsed as-is.
 *
 * @param {*} value
 * @param {"start"|"end"} edge
 * @returns {Date|null}
 */
const parseBound = (value, edge) => {
  const raw = unwrap(value);
  if (raw === null) {
    return null;
  }
  if (typeof raw === "string" && DATE_ONLY.test(raw)) {
    const [year, month, day] = raw.split("-").map(Number);
    return edge === "end"
      ? new Date(year, month - 1, day, 23, 59, 59, 999)
      : new Date(year, month - 1, day, 0, 0, 0, 0);
  }
  const parsed = new Date(raw);
  return isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * True when `now` falls inside the optional [validFrom, validTo] window.
 * Missing/invalid bounds are treated as unbounded on that side; missing both
 * means always valid.
 *
 * @param {*} validFrom optional lower bound ("Y-m-d" / ISO / Firestore-shaped / null)
 * @param {*} validTo   optional upper bound ("Y-m-d" / ISO / Firestore-shaped / null)
 * @param {Date} [now]
 * @returns {boolean}
 */
export const isWithinValidity = (validFrom, validTo, now = new Date()) => {
  const from = parseBound(validFrom, "start");
  const to = parseBound(validTo, "end");
  if (from && now < from) {
    return false;
  }
  if (to && now > to) {
    return false;
  }
  return true;
};
